import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarIcon, Loader2, Sparkles, X } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { submitOrder } from "@/lib/api";

const OCCASIONS = [
  "Birthday",
  "Baby shower",
  "Bridal shower / Wedding",
  "Halloween",
  "Thanksgiving",
  "Holiday / Christmas",
  "Corporate / branded",
  "Teacher / school gift",
  "Other",
];

const AI_STORAGE_KEY = "sr_ai_design";

export default function OrderForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    occasion: "",
    quantity: "",
    theme: "",
    details: "",
  });
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [aiDesign, setAiDesign] = useState(null);

  // Load any AI design previously attached via the AIDesigner
  useEffect(() => {
    const load = () => {
      try {
        const raw = sessionStorage.getItem(AI_STORAGE_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          setAiDesign(parsed);
          // Pre-fill theme field if it's still empty
          setForm((f) =>
            f.theme || !parsed?.theme ? f : { ...f, theme: parsed.theme }
          );
        }
      } catch (e) {
        /* ignore */
      }
    };
    load();
    window.addEventListener("sr-ai-design-attached", load);
    return () => window.removeEventListener("sr-ai-design-attached", load);
  }, []);

  const removeAiDesign = () => {
    sessionStorage.removeItem(AI_STORAGE_KEY);
    setAiDesign(null);
  };

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: typeof e === "string" ? e : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.occasion) {
      toast.error("Please fill in your name, email, and occasion.");
      return;
    }
    setLoading(true);
    try {
      await submitOrder({
        ...form,
        event_date: date ? format(date, "yyyy-MM-dd") : null,
        ai_design: aiDesign || null,
      });
      sessionStorage.removeItem(AI_STORAGE_KEY);
      setAiDesign(null);
      setSubmitted(true);
      toast.success("Your inquiry is in — we'll be in touch within 24 hours.");
    } catch (err) {
      toast.error("Something went wrong. Please call us or try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="order"
      data-testid="order-section"
      className="py-24 md:py-32 px-6 md:px-12 lg:px-20 bg-[#F5DCD5]"
    >
      <div className="max-w-5xl mx-auto grid lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5">
          <div className="text-xs uppercase tracking-[0.22em] text-[#B37C7C] font-medium mb-5">
            Place an Order
          </div>
          <h2 className="font-serif-display text-4xl md:text-5xl text-[#362C2C] leading-[1.05] mb-6">
            Let's make
            <span className="block italic font-accent-serif text-[#B37C7C]">
              your cookies.
            </span>
          </h2>
          <p className="text-[#5C5252] leading-relaxed mb-10">
            Tell us a bit about what you're celebrating and we'll get back to you within
            a day with pricing, timing, and a few ideas.
          </p>

          <div className="space-y-6 border-t border-[#EFE8E8] pt-8">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-[#5C5252] mb-1">
                Lead time
              </div>
              <div className="font-serif-display text-lg text-[#362C2C]">
                2–3 weeks recommended
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-[#5C5252] mb-1">
                Minimum order
              </div>
              <div className="font-serif-display text-lg text-[#362C2C]">1 dozen</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-[#5C5252] mb-1">
                Pickup
              </div>
              <div className="font-serif-display text-lg text-[#362C2C]">
                1809 Settlement Wy, Aubrey, TX
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          {submitted ? (
            <div
              className="bg-white border border-[#EFE8E8] rounded-lg p-10 text-center fade-up"
              data-testid="order-success"
            >
              <div className="font-serif-display text-3xl text-[#362C2C] mb-3">
                Thank you — your inquiry is with us.
              </div>
              <p className="text-[#5C5252] mb-6">
                We'll reply from Sweet Rose within one business day with next steps.
              </p>
              <button
                onClick={() => {
                  setSubmitted(false);
                  setForm({
                    name: "",
                    email: "",
                    phone: "",
                    occasion: "",
                    quantity: "",
                    theme: "",
                    details: "",
                  });
                  setDate(null);
                }}
                className="text-sm text-[#B37C7C] hover:underline"
                data-testid="order-reset"
              >
                Submit another inquiry
              </button>
            </div>
          ) : (
            <form
              onSubmit={submit}
              className="bg-white border border-[#EFE8E8] rounded-lg p-8 md:p-10 space-y-8"
              data-testid="order-form"
            >
              {aiDesign && (
                <div
                  className="bg-[#FFF7F3] border-l-2 border-[#B37C7C] rounded p-4 flex gap-3 items-start"
                  data-testid="order-ai-attached"
                >
                  {aiDesign.image_b64 && (
                    <img
                      src={`data:${aiDesign.image_mime || "image/png"};base64,${aiDesign.image_b64}`}
                      alt="AI design"
                      className="w-16 h-16 object-cover rounded border border-[#EFE8E8] flex-none"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[#B37C7C] mb-1">
                      <Sparkles size={11} />
                      AI design attached
                    </div>
                    <div className="font-serif-display text-base text-[#362C2C] truncate">
                      {aiDesign.theme || "Custom design concept"}
                    </div>
                    <div className="text-xs text-[#5C5252] mt-0.5">
                      Will be included with your inquiry.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={removeAiDesign}
                    className="text-[#5C5252] hover:text-[#B37C7C] p-1 -m-1"
                    aria-label="Remove attached design"
                    data-testid="order-ai-remove"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-6">
                <LineInput
                  label="Name"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Your full name"
                  testid="order-name"
                  required
                />
                <LineInput
                  label="Email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@email.com"
                  testid="order-email"
                  required
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <LineInput
                  label="Phone (optional)"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="(555) 123-4567"
                  testid="order-phone"
                />
                <div>
                  <label className="block text-sm text-[#5C5252] mb-2">Occasion</label>
                  <Select
                    value={form.occasion}
                    onValueChange={(v) => setForm((f) => ({ ...f, occasion: v }))}
                  >
                    <SelectTrigger
                      className="bg-transparent border-0 border-b border-[#EFE8E8] rounded-none px-0 py-2 h-auto focus:ring-0 focus:border-[#B37C7C] text-[#362C2C]"
                      data-testid="order-occasion-trigger"
                    >
                      <SelectValue placeholder="Select an occasion" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border border-[#EFE8E8]">
                      {OCCASIONS.map((o) => (
                        <SelectItem key={o} value={o} data-testid={`order-occasion-${o}`}>
                          {o}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm text-[#5C5252] mb-2">Event date</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="w-full flex items-center justify-between bg-transparent border-b border-[#EFE8E8] focus:border-[#B37C7C] outline-none px-0 py-2 text-left text-[#362C2C] hover:border-[#B37C7C] transition-colors"
                        data-testid="order-date-trigger"
                      >
                        <span className={date ? "" : "text-[#5C5252]/50"}>
                          {date ? format(date, "PPP") : "Pick a date"}
                        </span>
                        <CalendarIcon size={16} className="text-[#B37C7C]" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-auto p-0 bg-white border border-[#EFE8E8]"
                      align="start"
                    >
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <LineInput
                  label="Quantity (approx.)"
                  value={form.quantity}
                  onChange={update("quantity")}
                  placeholder="e.g. 3 dozen"
                  testid="order-quantity"
                />
              </div>

              <LineInput
                label="Theme / inspiration"
                value={form.theme}
                onChange={update("theme")}
                placeholder="e.g. Dusty rose garden, script monograms"
                testid="order-theme"
              />

              <div>
                <label className="block text-sm text-[#5C5252] mb-2">
                  Additional details
                </label>
                <textarea
                  value={form.details}
                  onChange={update("details")}
                  placeholder="Colors, logos, dietary notes, delivery needs…"
                  rows={4}
                  className="w-full bg-transparent border-b border-[#EFE8E8] focus:border-[#B37C7C] outline-none px-0 py-2 text-[#362C2C] placeholder:text-[#5C5252]/50 resize-none transition-colors"
                  data-testid="order-details"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center gap-2 bg-[#B37C7C] text-white px-7 py-3.5 rounded-md hover:bg-[#9A6565] hover:-translate-y-0.5 hover:shadow-lg shadow-[#B37C7C]/20 transition-all duration-300 tracking-wide text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="order-submit"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>Send Inquiry →</>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function LineInput({ label, testid, ...props }) {
  return (
    <div>
      <label className="block text-sm text-[#5C5252] mb-2">{label}</label>
      <input
        {...props}
        data-testid={testid}
        className="w-full bg-transparent border-b border-[#EFE8E8] focus:border-[#B37C7C] outline-none px-0 py-2 text-[#362C2C] placeholder:text-[#5C5252]/50 transition-colors"
      />
    </div>
  );
}
